/**
 * Referrals to external clinicians: the practice side. tasks/referrals.md §5.2, §5.3, §5.5.
 *
 * The shapes the API answers (`controllers/referralController.js`) and the PURE helpers the screens use: labels,
 * form validation and error messages. Kept free of Angular so they are unit-tested directly
 * (`referral.model.spec.ts`), the same arrangement as `inbox-item.model.ts`.
 */

import { FileObject, mapMediaToFileObject } from '../interfaces/file-object';
import { AI_LOW_BALANCE_LIMIT, AiTokenGate, aiTokenGate } from '../utils/ai-token-gate';

export type ReferralLinkStatus = 'pending' | 'active' | 'declined' | 'ended';
export type ReferralRecipientStatus = 'awaiting_link' | 'sharing' | 'stopped' | 'failed';
export type ReferralUrgency = 'routine' | 'soon' | 'urgent';
export type ReferralFutureMode = 'none' | 'until' | 'forever';
/** Whole timeline, or one reviewed summary of the patient file (referrals-platform-architecture §15). */
/** §21: `items` carries the entries someone picked out of the patient file, and nothing else. */
export type ReferralShareMode = 'full' | 'summary' | 'items';

/**
 * §25: how much a referral LINK may be sent, whoever is sending. Set by MedicAI, not by the practice: a clinician
 * outside MedicAI gets a referral note ('letter'), another MedicAI practice can be sent anything ('full').
 */
export type ReferralShareCeiling = 'letter' | 'full';

/** What a referral shares, in the practice's own words. */
export const SHARE_MODE_LABELS: Record<ReferralShareMode, string> = {
  full: 'The whole patient file',
  summary: 'A summary',
  items: 'Entries shared from the file',
};

/**
 * §21.7: a referral of chosen entries can be widened into the whole file. One way, and only while it is still sharing:
 * a summary referral is point in time, and a full one has nothing left to widen.
 */
export function canWidenToFull(
  referral: Pick<PatientReferral, 'shareMode'>,
  recipient: Pick<PatientReferralRecipient, 'status'>,
): boolean {
  return referral.shareMode === 'items' && recipient.status === 'sharing';
}
/** The flags an external note carries, as a clinical note does (referrals-platform-architecture §12). */
export interface ExternalNoteFlags {
  isMorbidity: boolean;
  isMortality: boolean;
}

/** An external clinician, in the clinical physician signup shape (referrals-platform-architecture §12). */
export interface ClinicianProfile {
  title: string;
  name: string;
  surname: string;
  country: string;
  hpcsaNumber: string;
  speciality: string;
  practiceName: string | null;
}

export interface ReferralNetworkLink {
  id: string;
  /**
   * §19: `group` is a link to another MedicAI practice. `invitation` is one this practice sent by email that has not been
   * accepted: the server does not say whether it went to a practice or an outside clinician (owner, 2026-09-22).
   */
  targetType: 'external' | 'group' | 'invitation';
  status: ReferralLinkStatus;
  /** Who asked: `outgoing` is this practice. External links are always outgoing. */
  direction: 'outgoing' | 'incoming';
  practice: { groupId: string; name: string } | null;
  inviteeEmail: string | null;
  inviteeName: string | null;
  inviteeHpcsaNumber: string | null;
  clinician: ClinicianProfile | null;
  requestedAt: string;
  respondedAt: string | null;
  endedAt: string | null;
  endedBySide: 'practice' | 'clinician' | 'target' | null;
  endedByThisPractice: boolean | null;
}

export interface ReferralNetwork {
  links: ReferralNetworkLink[];
  /** §19: referrals between practices are switched on separately from external clinicians. */
  practiceLinksEnabled: boolean;
  /** Only the practice's OWNER accepts or declines another practice's request (owner, 2026-09-22). */
  canRespond: boolean;
}

export interface ReferralRecipientOption {
  linkId: string;
  /** §19: `group` is another MedicAI practice; `invitation` is one sent by email and not yet accepted. */
  targetType?: 'external' | 'group' | 'invitation';
  /** §25: the most this link may be sent. 'letter' takes a referral note and nothing else. */
  shareCeiling?: ReferralShareCeiling;
  /** §25: the modes this link accepts, as the server will enforce them. */
  shareModes?: readonly ReferralShareMode[];
  status: 'active' | 'pending';
  displayName: string;
  speciality: string | null;
  practiceName: string | null;
}

export interface ReferralAcknowledgement {
  version: string;
  text: string;
}

export interface PatientReferralRecipient {
  id: string;
  status: ReferralRecipientStatus;
  firstSeenAt: string | null;
  stoppedAt: string | null;
  shareError: string | null;
  /** What the share job has done (§14): entries sent, entries that could not be (too large, or refused by the contract), and when. */
  sharedEntries: number;
  notSharedEntries: number;
  /** §16 M3: chosen summary files that could not be sent (deleted or replaced since they were chosen, or unreadable). */
  filesNotShared?: number;
  /** §18 V6: the referral itself, a demographics update or the stop, given up on here or refused by the referral portal. */
  messagesNotDelivered?: number;
  /** §19: `group` is another MedicAI practice, which accepts or declines the referral (null until it reaches them). */
  targetType?: 'external' | 'group' | 'invitation';
  practiceDecision?: { status: 'pending' | 'accepted' | 'declined'; decidedAt: string | null; declineReason: string | null } | null;
  lastSharedAt: string | null;
  nextShareAt: string | null;
  linkId: string;
  linkStatus: ReferralLinkStatus;
  displayName: string | null;
  speciality: string | null;
}

export interface PatientReferral {
  id: string;
  reason: string;
  urgency: ReferralUrgency;
  shareMode: ReferralShareMode;
  /** The summary exactly as sent, for a summary referral. */
  summary: { text: string; aiAssisted: boolean; reviewedAt: string | null; fileCount?: number } | null;
  futureMode: ReferralFutureMode;
  futureUntil: string | null;
  createdAt: string;
  createdByUserId: string;
  recipients: PatientReferralRecipient[];
}

/**
 * How many referral links are actively sharing THIS patient, for the badge on the patient file (§21). Counted by LINK,
 * not by referral: a clinician sent chosen entries and later the whole file has two referrals and is still one person
 * receiving this patient. A link the practice holds but has never used for this patient is not counted at all.
 */
export function activeReferralLinkCount(referrals: readonly PatientReferral[] | null | undefined): number {
  const links = new Set<string>();
  for (const referral of referrals || []) {
    for (const recipient of referral.recipients || []) {
      if (recipient.status === 'sharing' && recipient.linkStatus === 'active') links.add(recipient.linkId);
    }
  }
  return links.size;
}

/**
 * Whether THIS patient has been referred to one referral network link, from the patient's referrals (listForPatient):
 * a recipient on that link that is sharing, or waiting for the link to be accepted (network chat, 2026-09-22). `since`
 * is when the earliest such referral was made. A stopped or failed recipient does not count: nothing reaches them now.
 */
export function referralToLink(referrals: readonly PatientReferral[] | null | undefined, linkId: string | null | undefined): { referred: boolean; since: string | null } {
  const link = String(linkId || '').toLowerCase();
  if (!link) return { referred: false, since: null };
  let since: string | null = null;
  for (const referral of referrals || []) {
    const hit = (referral.recipients || []).some((r) => String(r.linkId || '').toLowerCase() === link && (r.status === 'sharing' || r.status === 'awaiting_link'));
    if (hit && (since === null || referral.createdAt < since)) since = referral.createdAt;
  }
  return { referred: since !== null, since };
}

/**
 * The recipient row a network chat's link into the REFERRAL chat is made with (2026-09-22): the newest referral of this
 * patient to `linkId` that is sharing now, whose ticket opens that chat. `awaiting`: referred, but the clinician has
 * not joined yet (an invitation), so there is no referral chat to link into until they do.
 */
export function sharingRecipientOnLink(referrals: readonly PatientReferral[] | null | undefined, linkId: string | null | undefined): { recipientId: string | null; awaiting: boolean } {
  const link = String(linkId || '').toLowerCase();
  let best: { id: string; at: string } | null = null;
  let awaiting = false;
  for (const referral of referrals || []) {
    for (const r of referral.recipients || []) {
      if (String(r.linkId || '').toLowerCase() !== link) continue;
      if (r.status === 'awaiting_link') awaiting = true;
      if (r.status === 'sharing' && r.linkStatus === 'active' && (!best || referral.createdAt > best.at)) best = { id: r.id, at: referral.createdAt };
    }
  }
  return { recipientId: best?.id ?? null, awaiting: !best && awaiting };
}

/**
 * What a network chat can do with a patient linked in it (2026-09-22), from the patient's referrals:
 * - `share`: a recipient on this link is sharing, so the message can be linked into the referral chat (`recipientId`);
 * - `settling`: referred on this link a moment ago and the link is live, but the recipient is not sharing yet (a new
 *   referral is `awaiting_link` for a few hundred milliseconds): try again shortly;
 * - `awaiting`: referred, but the clinician has not joined yet (the link itself is still pending): nothing to link into;
 * - `none`: not referred on this link (or only stopped/failed referrals).
 */
export type NetworkShareDecision =
  | { kind: 'share'; recipientId: string; since: string | null }
  | { kind: 'settling'; since: string | null }
  | { kind: 'awaiting'; since: string | null }
  | { kind: 'none'; since: null };

export function networkShareDecision(referrals: readonly PatientReferral[] | null | undefined, linkId: string | null | undefined): NetworkShareDecision {
  const link = String(linkId || '').toLowerCase();
  if (!link) return { kind: 'none', since: null };
  const { since } = referralToLink(referrals, link);
  const { recipientId } = sharingRecipientOnLink(referrals, link);
  if (recipientId) return { kind: 'share', recipientId, since };
  const onLink = (referrals || []).flatMap((f) => f.recipients || []).filter((r) => String(r.linkId || '').toLowerCase() === link);
  const waiting = onLink.filter((r) => r.status === 'awaiting_link');
  if (!waiting.length) return { kind: 'none', since: null };
  return { kind: waiting.some((r) => r.linkStatus === 'active') ? 'settling' : 'awaiting', since };
}

/** How long to wait before each re-check while a new referral settles, or its referral chat is not ready: ~20 s in all. */
export const NETWORK_SHARE_RETRY_MS: readonly number[] = [500, 1000, 1500, 2000, 3000, 4000, 4000, 4000];

/**
 * Someone in the practice's referral network a clinician may chat 1-on-1 with (network chat, 2026-09-22), from
 * `GET /group/:g/network-contacts`: per active link, a linked practice's DOCTORS, or the external clinician (userId null).
 */
export interface NetworkContactPerson {
  userId: string | null;
  title: string | null;
  name: string | null;
  surname: string | null;
  displayName: string;
  speciality: string | null;
  /** an external clinician's: the address the practice invited them at, to tell apart two people of the same name */
  email?: string | null;
}

export interface NetworkContactLink {
  linkId: string;
  kind: 'practice' | 'external';
  practiceName: string | null;
  people: NetworkContactPerson[];
}

/**
 * What a referral chat shows beside its messages (tasks/network-direct-chat.md phase 5):
 * `GET /group/:g/chat-context?patientId&linkId` (services/referralIntegration/chatContext.js). Dated items, oldest first.
 */
export type ReferralReviewStatus = 'pending' | 'validated' | 'dismissed';
export interface ChatContextSent {
  kind: 'referral_sent';
  id: string;
  at: string;
  reason: string;
  urgency: ReferralUrgency;
  shareMode: ReferralShareMode;
  summary: { text: string | null; aiAssisted: boolean; reviewedByName: string | null; reviewedAt: string | null; fileCount: number } | null;
  recipientStatus: string;
  practiceDecision: { status: string; decidedAt: string | null } | null;
}
export interface ChatContextReceived {
  kind: 'referral_received';
  id: string;
  at: string;
  /** the other practice's reply to one of this practice's referrals (a share-back), not a referral of its own */
  isReply: boolean;
  practiceName: string | null;
  referredByName: string | null;
  /** null while awaiting a decision, for someone who may not decide */
  reason: string | null;
  urgency: ReferralUrgency;
  shareMode: ReferralShareMode;
  summaryText: string | null;
  status: 'pending' | 'accepted' | 'declined';
  decidedAt: string | null;
  canDecide: boolean;
}
export interface ChatContextResponse {
  kind: 'response';
  responseKind: 'external_note' | 'practice_entry';
  id: string;
  at: string;
  title: string | null;
  authorName: string | null;
  practiceName?: string | null;
  noteDate: string | null;
  reviewStatus: ReferralReviewStatus;
  reviewedAt: string | null;
  preview: string;
  canReview: boolean;
}
export interface ChatContextEntries {
  kind: 'entries';
  id: string;
  at: string;
  receiptId: string;
  practiceName: string | null;
  total: number;
  pending: number;
}
export type ChatContextItem = ChatContextSent | ChatContextReceived | ChatContextResponse | ChatContextEntries;
export interface ReferralChatContext {
  linkKind: 'practice' | 'external' | 'invitation';
  items: ChatContextItem[];
}

/**
 * The items a thread shows now, as the referrals dashboard's stream does: while older messages are still to be loaded,
 * an item older than the oldest loaded message waits (it would otherwise sit above messages that come before it).
 */
export function visibleContextItems(items: readonly ChatContextItem[], oldestLoadedAt: string | null, hasMore: boolean): ChatContextItem[] {
  if (!hasMore || !oldestLoadedAt) return [...items];
  const cutoff = new Date(oldestLoadedAt).getTime();
  return items.filter((i) => new Date(i.at).getTime() >= cutoff);
}

/** The review state of a response, as its chip says it. */
export function reviewStatusLabel(status: ReferralReviewStatus): string {
  return status === 'validated' ? 'Accepted to timeline' : status === 'dismissed' ? 'Dismissed' : 'Awaiting review';
}

export interface NewReferralForm {
  linkIds: string[];
  reason: string;
  urgency: ReferralUrgency | null;
  futureMode: ReferralFutureMode | null;
  futureUntil: string | Date | null;
  acknowledged: boolean;
  shareMode: ReferralShareMode;
  /** The summary to send (plain text): an AI draft the physician edits, or written by hand. */
  summaryText: string;
  /** True once an AI draft was generated for this summary, even if the physician then edited it. */
  summaryAiAssisted: boolean;
  /** §16 M2: the files to send with the summary, by reference, in the order chosen. */
  summaryFileRefs: string[];
}

/** A file that can be sent with a summary (GET …/shareable-files, §16 M2). Named by reference, never by URL. */
export interface ShareableFile {
  fileRef: string;
  sourceType: 'note' | 'document';
  entryDate: string;
  entryTitle: string;
  kind: 'image' | 'pdf';
  fileName: string | null;
  contentType: string | null;
  /** Page images of a PDF (they travel with it); null for an image. */
  pageCount: number | null;
  /** A 10-minute link to the image, or to a PDF's first page; null for a PDF without pages. */
  previewUrl: string | null;
}

/**
 * The chosen summary files as the shared file gallery shows them: each file's preview, which for a PDF is its first
 * page (an image), so the gallery opens it in the image viewer. A PDF with no page images has nothing to show and stays
 * a PDF tile. `id` is the fileRef, so a removal maps back to the file.
 */
export function shareableGalleryFiles(files: readonly ShareableFile[]): FileObject[] {
  return files.map((f, i) => ({
    id: f.fileRef,
    name: f.fileName || f.entryTitle,
    url: f.previewUrl || '',
    type: f.previewUrl ? (f.kind === 'image' ? f.contentType || 'image/jpeg' : 'image/png') : 'application/pdf',
    displayIndex: i,
    annotatedVersions: [],
  }));
}

/** §25: a referral note carries a few files, not a patient file's worth (server: MAX_SUMMARY_FILES). */
export const REFERRAL_SUMMARY_FILES_MAX = 5;

/** Picker groups: one per timeline entry, in the order the server lists them (newest first). */
export function groupShareableFiles(files: readonly ShareableFile[]): { key: string; entryTitle: string; entryDate: string; sourceType: ShareableFile['sourceType']; files: ShareableFile[] }[] {
  const groups = new Map<string, { key: string; entryTitle: string; entryDate: string; sourceType: ShareableFile['sourceType']; files: ShareableFile[] }>();
  for (const file of files) {
    const key = file.fileRef.split(':').slice(0, 2).join(':');
    const group = groups.get(key) ?? { key, entryTitle: file.entryTitle, entryDate: file.entryDate, sourceType: file.sourceType, files: [] };
    group.files.push(file);
    groups.set(key, group);
  }
  return [...groups.values()];
}

/** Picks or unpicks a file, keeping the order of choosing; a pick past the limit changes nothing. */
export function toggleSummaryFile(selected: readonly string[], fileRef: string, max: number = REFERRAL_SUMMARY_FILES_MAX): string[] {
  if (selected.includes(fileRef)) return selected.filter((r) => r !== fileRef);
  return selected.length >= max ? [...selected] : [...selected, fileRef];
}

/** What the summary-draft endpoint returns. The draft is not stored anywhere. */
export interface SummaryDraft {
  text: string;
  aiAssisted: boolean;
  includedEntries: number;
  omittedEntries: number;
  generatedAt: string;
}

/** A file sent with an external note, as the reviewer sees it before validating (referrals-platform-architecture §12). */
export interface ExternalNoteAttachment {
  id: string;
  fileName: string | null;
  contentType: string;
  sizeBytes: number | null;
  /**
   * pending: still being received; refetching: fresh links were asked for (§16 R3), still being received; stored: ready
   * (previewUrl lasts 10 minutes); failed / expired: will not be filed unless asked for again.
   */
  status: 'pending' | 'refetching' | 'stored' | 'failed' | 'expired';
  previewUrl: string | null;
  /** §16 R4: ended without its bytes being wrong, so "Request again" can fetch it. */
  canRequestAgain?: boolean;
  /** §13: a page rendered from a PDF of the note, or an annotated version of an image or page; null for an item. */
  parentId?: string | null;
  relation?: 'page' | 'annotation' | null;
  pageNumber?: number | null;
}

export interface ExternalNoteForReview extends ExternalNoteFlags {
  id: string;
  attachments: ExternalNoteAttachment[];
  /** The note's own date, YYYY-MM-DD. */
  noteDate: string;
  title: string;
  notes: string;
  author: ClinicianProfile;
  submittedAt: string;
  receivedAt: string;
  reviewStatus: 'pending' | 'validated' | 'dismissed';
  reviewedAt: string | null;
  reviewedByName: string | null;
  dismissReason: string | null;
  filedPatientNoteId: string | null;
  patient: { id: string; name?: string; surname?: string; patientNumber?: string };
  referral: { reason: string; referredAt: string };
  canReview: boolean;
}

/* ── labels ───────────────────────────────────────────────────────────────────────── */

export const LINK_STATUS_LABELS: Record<ReferralLinkStatus, string> = {
  pending: 'Invitation sent',
  active: 'Connected',
  declined: 'Declined',
  ended: 'Ended',
};

export const RECIPIENT_STATUS_LABELS: Record<ReferralRecipientStatus, string> = {
  awaiting_link: 'Waiting for them to accept your invitation',
  sharing: 'Shared',
  stopped: 'Sharing stopped',
  failed: 'Not shared',
};

export const URGENCY_LABELS: Record<ReferralUrgency, string> = { routine: 'Routine', soon: 'Soon', urgent: 'Urgent' };

export const FUTURE_MODE_LABELS: Record<ReferralFutureMode, string> = {
  none: 'Do not share future entries',
  until: 'Share future entries until a date',
  forever: 'Share future entries until I stop sharing',
};

/** 'Clinical note', 'Morbidity note', 'Mortality note' or 'Morbidity and mortality note'. */
export function noteFlagsLabel(flags: ExternalNoteFlags): string {
  if (flags.isMorbidity && flags.isMortality) return 'Morbidity and mortality note';
  if (flags.isMortality) return 'Mortality note';
  if (flags.isMorbidity) return 'Morbidity note';
  return 'Clinical note';
}

/** The strongest flag, for the label's colour. */
export const noteFlagsTone = (flags: ExternalNoteFlags): 'mortality' | 'morbidity' | 'clinical' =>
  flags.isMortality ? 'mortality' : flags.isMorbidity ? 'morbidity' : 'clinical';

/** Who a link is with, in the words a list row shows. */
export function linkDisplayName(link: Pick<ReferralNetworkLink, 'clinician' | 'inviteeName' | 'inviteeEmail'> & Partial<Pick<ReferralNetworkLink, 'practice'>>): string {
  if (link.practice) return link.practice.name || 'A MedicAI practice';
  if (link.clinician) return `${link.clinician.name} ${link.clinician.surname}`.trim();
  return link.inviteeName || link.inviteeEmail || 'Invited clinician';
}

/** A link's state as this practice sees it: for a practice link, who is waiting on whom (§19, P3). */
export function linkStatusLabel(link: Pick<ReferralNetworkLink, 'targetType' | 'status' | 'direction'>): string {
  if (link.targetType === 'group' && link.status === 'pending') {
    return link.direction === 'outgoing' ? 'Request sent: waiting for them to accept' : 'Wants to link with your practice';
  }
  return LINK_STATUS_LABELS[link.status];
}

/** Who ended or declined a link, from this practice's point of view. */
export function linkEndedBy(link: Pick<ReferralNetworkLink, 'status' | 'endedByThisPractice'>): string | null {
  if (link.status !== 'ended' || link.endedByThisPractice === null) return null;
  return link.endedByThisPractice ? 'your practice' : 'them';
}

/** The icon on a link's chip: connected, waiting, a practice, or a link that has ended. */
export function linkIcon(link: Pick<ReferralNetworkLink, 'targetType' | 'status' | 'direction'>): string {
  if (link.status === 'ended' || link.status === 'declined') return 'link_off';
  if (link.status === 'active') return link.targetType === 'group' ? 'domain' : 'how_to_reg';
  return canRespondToLink(link) ? 'mark_email_unread' : 'schedule_send';
}

/**
 * Everything a chip has no room for, as its tooltip: what the row used to show on three lines - who they are, the state
 * of the link, and the date that state began.
 */
export function linkTooltip(link: ReferralNetworkLink, formatDate: (iso: string) => string): string {
  const who = link.clinician
    ? [link.clinician.speciality, link.clinician.practiceName].filter(Boolean).join(' · ')
    : (link.targetType !== 'group' ? link.inviteeEmail : null);
  const when = link.status === 'ended' || link.status === 'declined'
    ? link.endedAt || link.respondedAt
    : link.status === 'active' ? link.respondedAt : link.requestedAt;
  const since = when ? `${link.status === 'active' ? 'since ' : ''}${formatDate(when)}` : null;
  const endedBy = linkEndedBy(link);
  return [who, [linkStatusLabel(link), since].filter(Boolean).join(' · '), endedBy ? `Ended by ${endedBy}` : null]
    .filter(Boolean).join('\n');
}

/** Accept and decline belong to the practice a link was requested OF; ending to either side once active. */
export const canRespondToLink = (link: Pick<ReferralNetworkLink, 'targetType' | 'status' | 'direction'>): boolean =>
  link.targetType === 'group' && link.status === 'pending' && link.direction === 'incoming';

/** A referral code as typed, tidied for display and checked for shape (Crockford base32, 12 characters; §19, P2). */
export function tidyPracticeCode(value: string): { code: string; complete: boolean } {
  const s = String(value || '').toUpperCase().replace(/[\s-]/g, '').replace(/O/g, '0').replace(/[IL]/g, '1').replace(/[^0-9A-HJKMNP-TV-Z]/g, '').slice(0, 12);
  return { code: [s.slice(0, 4), s.slice(4, 8), s.slice(8, 12)].filter(Boolean).join('-'), complete: s.length === 12 };
}

/** A recipient's state, with the one fact that makes it specific (when seen, why not shared). */
export function recipientStatusLabel(recipient: Pick<PatientReferralRecipient, 'status' | 'firstSeenAt' | 'shareError'> & Partial<Pick<PatientReferralRecipient, 'targetType' | 'practiceDecision'>>): string {
  if (recipient.targetType === 'group') {
    const decision = recipient.practiceDecision;
    if (decision?.status === 'declined') return `Declined by the practice${decision.declineReason ? `: ${decision.declineReason}` : ''}`;
    if (decision?.status === 'accepted') return recipient.status === 'stopped' ? 'Accepted · sharing stopped' : 'Accepted by the practice';
    if (recipient.status === 'awaiting_link') return 'Waiting for the practice to accept your link request';
    if (recipient.status === 'failed' && recipient.shareError === 'LINK_DECLINED') return 'Not shared: the practice declined your link request';
    if (decision?.status === 'pending') return recipient.status === 'stopped' ? 'Sharing stopped before they decided' : 'Sent: waiting for the practice to accept';
  }
  if (recipient.status === 'sharing' && recipient.firstSeenAt) return 'Shared · opened';
  if (recipient.status === 'failed' && recipient.shareError === 'LINK_DECLINED') return 'Not shared: they declined your invitation';
  return RECIPIENT_STATUS_LABELS[recipient.status] || recipient.status;
}

/* ── one entry of the patient file, shared with a link (§21) ────────────────────── */

/** The timeline entry kinds a referral can carry. `ward_change` is shared by the job, not chosen by hand. */
export type ShareableSourceType = 'note' | 'admission' | 'document' | 'ward_change';

/**
 * What happened for one link when an entry was shared (§21): it joined the referral that link already has for this
 * patient, or one was made. `alreadyCovered` means that referral shares the whole file already, so there was nothing to
 * add - the entry reaches them regardless.
 */
export interface SharedEntryOutcome {
  linkId: string;
  recipientId: string;
  created: boolean;
  added: boolean;
  alreadyCovered: boolean;
}

/** How many links one entry of this patient has reached, for the timeline's share counters. */
export interface EntryShareCount {
  sourceType: ShareableSourceType;
  sourceId: string;
  links: number;
}

/** What the timeline knows about one item's sharing: its key, and who already has it. */
export function entryShareKey(sourceType: string, sourceId: string): string {
  return `${sourceType}|${String(sourceId).toLowerCase()}`;
}

/* ── referrals received from another practice (§19, P5, P8) ─────────────────────── */

export type ShareBackMode = 'full' | 'summary' | 'none';

export interface IncomingPracticeReferral {
  id: string;
  status: 'pending' | 'accepted' | 'declined';
  senderPracticeName: string;
  referredByName: string;
  reason: string;
  urgency: ReferralUrgency;
  shareMode: ReferralShareMode;
  summaryText: string | null;
  /** The demographics as the referring practice sent them. */
  patient: Record<string, string | boolean | null>;
  referredAt: string;
  receiverPatientId: string | null;
  decidedAt: string | null;
  declineReason: string | null;
  replyReferralId: string | null;
  stillShared: boolean;
  linkActive: boolean;
  linkId?: string;
  /** The referring practice's ACTIVE ICD-10 codes (§20). Reference only: never filed into this practice's coding. */
  diagnoses: SharedDiagnoses | null;
}

export interface SharedDiagnosis {
  code: string;
  description: string | null;
  role: 'primary' | 'secondary' | 'comorbidity' | 'complication' | 'external-cause' | 'morphology' | null;
  onsetDate: string | null;
  isChronic: boolean;
}

export interface SharedDiagnoses {
  codes: SharedDiagnosis[];
  /** The referring practice's written diagnosis (Patient.diagnosis … diagnosis24, filled ones only). Null on a copy stored before §20.7. */
  workingDiagnosis: Partial<Record<string, string>> | null;
  /** The patient's allergies as the referring practice records them. Null on a copy stored before §20.7. */
  allergies: { noAllergies: boolean; items: { sourceId: string; allergy: string; reaction: string | null }[] } | null;
  /** Medical history (PatientMedicalHistory: `surgery`, `year`) and medication (§20.8). Null on a copy stored before them. */
  medicalHistory: { sourceId: string; surgery: string; year: string | null }[] | null;
  medications: {
    reported: { sourceId: string; medication: string; dosage: string | null }[];
    active: { sourceId: string; name: string; dose: string | null; unit: string | null; frequency: string | null; dosageForm: string | null }[];
  } | null;
  updatedAt: string;
}

/** One chip per medicine: the same medicine on several prescriptions arrives once per prescription (§20.8); the latest is shown. */
export function distinctMedications<T extends { name: string }>(active: readonly T[]): T[] {
  const byName = new Map<string, T>();
  for (const m of active) byName.set(m.name.trim().toLowerCase(), m);
  return [...byName.values()];
}

/** The written diagnosis as lines, in the practice's order (diagnosis, then 2 to 24). */
export function workingDiagnosisLines(working: Partial<Record<string, string>> | null | undefined): string[] {
  if (!working) return [];
  const fields = ['diagnosis', ...Array.from({ length: 23 }, (_, i) => `diagnosis${i + 2}`)];
  return fields.map((f) => (working[f] || '').trim()).filter((v) => !!v);
}

const DIAGNOSIS_ROLE_LABELS: Record<string, string> = {
  primary: 'Primary', secondary: 'Secondary', comorbidity: 'Comorbidity',
  complication: 'Complication', 'external-cause': 'External cause', morphology: 'Morphology',
};

/** The line under a shared diagnosis: the practice's own role, chronic, and since when. Empty when it said none of them. */
export function diagnosisMeta(code: Pick<SharedDiagnosis, 'role' | 'isChronic' | 'onsetDate'> | null | undefined): string {
  if (!code) return '';
  const since = typeof code.onsetDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(code.onsetDate.trim()) ? code.onsetDate.trim().slice(0, 10) : null;
  return [
    code.role ? DIAGNOSIS_ROLE_LABELS[code.role] || code.role : null,
    code.isChronic ? 'Chronic' : null,
    since ? `since ${since}` : null,
  ].filter(Boolean).join(' · ');
}

/**
 * The demographics sent, as the subject that unlinked pathology results are ranked against (core/unlinked-match): which of
 * this practice's patients it may be is worked out in the browser from the practice's loaded patient list, exactly as for
 * a result that arrived without a patient. Suggestions only: nothing is chosen for the user.
 */
export function incomingMatchSubject(patient: Record<string, string | boolean | null> | null | undefined): {
  firstName: string | null; surname: string | null; dateOfBirth: string | null; sex: string | null; idNumber: string | null; passportNumber: string | null;
} {
  const text = (key: string): string | null => {
    const value = patient?.[key];
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  };
  return {
    firstName: text('name'), surname: text('surname'), dateOfBirth: text('dob'), sex: text('gender'),
    idNumber: text('idNumber'), passportNumber: text('passportNumber'),
  };
}

export interface AcceptIncomingForm {
  /** A patient id of this practice, or 'new' to create the patient from what was sent. */
  patient: string | null;
  shareBack: ShareBackMode | null;
  futureMode: ReferralFutureMode | null;
  futureUntil: string | Date | null;
  reason: string;
  acknowledged: boolean;
}

/** What still stops an accept, in the order the form asks (P8: sharing back is chosen like a referral is). */
export function acceptIncomingProblems(form: AcceptIncomingForm, linkActive: boolean, now: Date = new Date()): string[] {
  const problems: string[] = [];
  if (!form.patient) problems.push('Choose the patient in your practice, or create them.');
  if (!form.shareBack) problems.push('Choose how to share back with the referring practice.');
  if ((form.shareBack === 'full' || form.shareBack === 'summary') && !linkActive) problems.push('Your practices are no longer linked, so you cannot share back.');
  if (form.shareBack === 'full') {
    if (!form.futureMode) problems.push('Choose whether later entries are shared.');
    if (form.futureMode === 'until' && !(form.futureUntil && new Date(form.futureUntil).getTime() > now.getTime())) problems.push('Choose a date in the future.');
    if (!form.acknowledged) problems.push('Confirm that shared records cannot be unshared.');
  }
  return problems;
}

export function acceptIncomingBody(form: AcceptIncomingForm, acknowledgementVersion: string): Record<string, unknown> {
  const full = form.shareBack === 'full';
  return {
    ...(form.patient === 'new' ? { createPatient: true } : { patientId: form.patient }),
    shareBack: {
      mode: form.shareBack,
      ...(full ? {
        futureMode: form.futureMode,
        futureUntil: form.futureMode === 'until' && form.futureUntil ? new Date(form.futureUntil).toISOString() : null,
        reason: (form.reason || '').trim() || null,
        acknowledged: form.acknowledged,
        acknowledgementVersion,
      } : {}),
    },
  };
}

/* ── entries received from another practice (§19, P6, P7) ───────────────────────── */

export interface PracticeEntryFile {
  id: string;
  fileName: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  /** pending: still being copied; stored: ready and filed with the entry; failed: could not be copied (it is not filed). */
  status: 'pending' | 'stored' | 'failed';
  lastError: string | null;
  /** The patient app only (tasks/patient-app.md §6.5): 'page' is a page image of the PDF above it. */
  role?: 'file' | 'page';
  pageNumber?: number | null;
}

export interface PracticeEntryForReview {
  id: string;
  sourceType: 'note' | 'admission' | 'document' | 'ward_change' | 'summary_files' | 'summary';
  entryDate: string | null;
  receivedAt: string;
  authorName: string | null;
  originGroupName: string;
  /** How it will be filed: a read-only note with this title and text. */
  rendered: { title: string; notes: string; date: string | null; isMorbidity: boolean; isMortality: boolean };
  reviewStatus: 'pending' | 'validated' | 'dismissed';
  reviewedAt: string | null;
  dismissReason: string | null;
  filedPatientNoteId: string | null;
  referralAccepted: boolean;
  patientId: string | null;
  files: PracticeEntryFile[];
  canReview: boolean;
}

export const PRACTICE_ENTRY_TYPE_LABELS: Record<PracticeEntryForReview['sourceType'], string> = {
  note: 'Note', admission: 'Admission', document: 'Returned document', ward_change: 'Ward move', summary_files: 'Files', summary: 'Referral summary',
};

/** What stops validating now, in words; null when it can be validated. */
export function practiceEntryBlocker(entry: Pick<PracticeEntryForReview, 'reviewStatus' | 'referralAccepted' | 'files'>): string | null {
  if (entry.reviewStatus !== 'pending') return null;
  if (!entry.referralAccepted) return 'Accept the referral first: then its entries can be filed on the patient.';
  if (entry.files.some((f) => f.status === 'pending')) return 'The files are still being copied. Try again in a minute.';
  return null;
}

/* ── onward notices and demographic changes (§19, P9–P12) ─────────────────────────── */

export interface OnwardNotice {
  id: string;
  onwardPracticeName: string;
  onwardRecipientName: string;
  createdAt: string;
  acknowledgedAt: string | null;
  patientId: string;
}

export interface DemographicField {
  field: string;
  label: string;
  old: string | boolean | null;
  proposed: string | boolean | null;
  current: string | boolean | null;
  /** This practice already has the proposed value: nothing to decide. */
  alreadySame: boolean;
}

export interface DemographicProposal {
  id: string;
  status: 'pending' | 'resolved' | 'superseded';
  createdAt: string;
  senderPracticeName: string;
  referralAccepted: boolean;
  patientId: string | null;
  fields: DemographicField[];
  decisions: Record<string, 'accepted' | 'rejected' | 'same'> | null;
  resolvedAt: string | null;
}

export type DemographicDecision = 'accept' | 'reject';

/** A value as a reviewer reads it: a blank for nothing, Yes/No for the deceased flag. */
export function demographicValue(value: string | boolean | null | undefined): string {
  if (value === null || value === undefined || value === '') return '(blank)';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

/** The fields still needing a decision (P11): every changed field this practice does not already have. */
export function undecidedFields(fields: readonly DemographicField[], decisions: Record<string, DemographicDecision | undefined>): string[] {
  return fields.filter((f) => !f.alreadySame && !decisions[f.field]).map((f) => f.label);
}

/** Accepting a change to the deceased details marks the patient as deceased here too, as a mortality note would. */
export function demographicWarning(fields: readonly DemographicField[], decisions: Record<string, DemographicDecision | undefined>): string | null {
  const deceased = fields.find((f) => f.field === 'isDeceased');
  if (deceased && decisions['isDeceased'] === 'accept' && deceased.proposed === true) {
    return 'Accepting this marks the patient as deceased in your practice.';
  }
  return null;
}

/** §18 V6: why an entry did not reach the recipient. */
export type UnsharedEntryReason = 'too_large' | 'invalid' | 'send_failed' | 'rejected';

export interface UnsharedEntry {
  sourceType: string;
  sourceId: string;
  /** From the patient file (a note's title, a document's name); null when the source is gone. */
  label: string | null;
  reason: UnsharedEntryReason;
  /** The referral portal's reason code, when it refused the message. */
  code: string | null;
  at: string;
}

export const UNSHARED_REASON_LABELS: Record<UnsharedEntryReason, string> = {
  too_large: 'too large to send',
  invalid: 'could not be put into a referral message',
  send_failed: 'not delivered: sending failed',
  rejected: 'not delivered: refused by the referral portal',
};

const SOURCE_TYPE_LABELS: Record<string, string> = {
  note: 'Note', admission: 'Admission', document: 'Document', ward_change: 'Ward move', summary_files: 'Summary files',
};

/** One line per entry a recipient does not have: what it is, and why. */
export function unsharedEntryLine(entry: Pick<UnsharedEntry, 'sourceType' | 'label' | 'reason'>): string {
  const what = entry.label || SOURCE_TYPE_LABELS[entry.sourceType] || 'Entry';
  return `${what} — ${UNSHARED_REASON_LABELS[entry.reason] || 'not shared'}`;
}

/**
 * How far sharing has got for a recipient, or null when there is nothing to say (never sent, e.g. awaiting their link).
 * Entries that could not be shared are always mentioned: the practice may need to send them another way.
 */
export function recipientShareSummary(recipient: Pick<PatientReferralRecipient, 'status' | 'sharedEntries' | 'notSharedEntries' | 'lastSharedAt' | 'shareError' | 'filesNotShared' | 'messagesNotDelivered'>): string | null {
  const shared = recipient.sharedEntries || 0;
  const notShared = recipient.notSharedEntries || 0;
  const parts: string[] = [];
  if (recipient.status === 'sharing' && !recipient.lastSharedAt && !recipient.shareError) parts.push('Sharing the record');
  if (shared > 0 || recipient.lastSharedAt) parts.push(`${shared} ${shared === 1 ? 'entry' : 'entries'} shared`);
  if (notShared > 0) parts.push(`${notShared} ${notShared === 1 ? 'entry' : 'entries'} could not be shared`);
  const filesNotShared = recipient.filesNotShared || 0;
  if (filesNotShared > 0) parts.push(`${filesNotShared} ${filesNotShared === 1 ? 'file' : 'files'} could not be shared`);
  const notDelivered = recipient.messagesNotDelivered || 0;
  if (notDelivered > 0) parts.push(notDelivered === 1 ? 'the referral or an update was not delivered' : `${notDelivered} referral messages were not delivered`);
  if (recipient.status === 'sharing' && recipient.shareError && recipient.shareError !== 'LINK_DECLINED') parts.push('sharing is delayed and will be retried');
  return parts.length ? parts.join(' · ') : null;
}

export const canStopSharing = (recipient: Pick<PatientReferralRecipient, 'status'>): boolean =>
  recipient.status === 'sharing' || recipient.status === 'awaiting_link';

export const canEndLink = (link: Pick<ReferralNetworkLink, 'status'> & Partial<Pick<ReferralNetworkLink, 'targetType' | 'direction'>>): boolean =>
  link.status === 'active' || (link.status === 'pending' && !(link.targetType === 'group' && link.direction === 'incoming'));

/**
 * What a reviewer must know before validating a note with these flags. A validated mortality note is an ordinary mortality
 * note, and elsewhere in the platform the mortality note is what marks the patient deceased.
 */
export function reviewWarning(flags: ExternalNoteFlags): string | null {
  const warnings = [
    flags.isMortality ? "Validating a mortality note records the patient's death: the patient will be marked as deceased, as with any mortality note." : null,
    flags.isMorbidity ? 'Validating a morbidity note adds it to your practice\'s morbidity records and statistics.' : null,
  ].filter((w): w is string => w !== null);
  return warnings.length ? warnings.join(' ') : null;
}

const isStored = (a: ExternalNoteAttachment | undefined): boolean => !!a && a.status === 'stored' && !!a.previewUrl;

/** The pages of each PDF, by the PDF's id. */
function pagesByPdf(files: ExternalNoteAttachment[]): Map<string, ExternalNoteAttachment[]> {
  const pages = new Map<string, ExternalNoteAttachment[]>();
  for (const f of files.filter((x) => x.relation === 'page' && x.parentId)) {
    pages.set(f.parentId as string, [...(pages.get(f.parentId as string) || []), f]);
  }
  return pages;
}

/**
 * Stored files in the shape app-file-gallery shows (a short preview link each), as the note will look once filed
 * (services/referralIntegration/filing.js planFiling, §13):
 *   - a PDF whose pages all arrived is shown as its pages, the way mapMediaToFileObject shows a note PDF;
 *   - a PDF without pages (or with pages missing) is shown as the PDF, whose pages are made after validating;
 *   - an annotated version is shown in place of its original, as galleries show the newest annotation;
 *     one whose original is not shown is shown on its own.
 * Images go through mapMediaToFileObject itself, so ordering and annotation follow the clinical gallery exactly.
 */
export function reviewGalleryFiles(attachments: ExternalNoteAttachment[] | undefined): FileObject[] {
  const files = attachments || [];
  const pages = pagesByPdf(files);
  const media: any[] = [];
  const pdfs: (FileObject & { name: string })[] = [];
  const shown = new Set<string>();
  const name = (a: ExternalNoteAttachment) => a.fileName || 'Attachment';
  let index = 0;
  const addImage = (a: ExternalNoteAttachment) => {
    media.push({ id: a.id, mediaUrl: a.previewUrl, mediaType: a.contentType, displayIndex: index++, name: name(a), annotatedVersions: [] });
    shown.add(a.id);
  };
  for (const item of files.filter((f) => !f.parentId && isStored(f))) {
    if (item.contentType !== 'application/pdf') {
      addImage(item);
      continue;
    }
    const itsPages = [...(pages.get(item.id) || [])].sort((a, b) => (a.pageNumber || 0) - (b.pageNumber || 0));
    if (itsPages.length && itsPages.every(isStored)) {
      shown.add(item.id);
      itsPages.forEach(addImage);
    } else {
      pdfs.push({ id: item.id, url: item.previewUrl as string, type: item.contentType, name: name(item), displayIndex: index++ });
    }
  }
  for (const note of files.filter((f) => f.relation === 'annotation' && isStored(f))) {
    const original = note.parentId ? media.find((m) => m.id === note.parentId) : undefined;
    if (original) {
      original.annotatedVersions.push({ id: note.id, mediaUrl: note.previewUrl, createdAt: index++ });
    } else {
      addImage(note);
    }
  }
  const images = media.length ? mapMediaToFileObject(media).map((f) => ({ ...f, name: media.find((m) => m.id === f.id)?.name })) : [];
  return [...images, ...pdfs].sort((a, b) => (a.displayIndex ?? 0) - (b.displayIndex ?? 0));
}

/** Files that will not reach the timeline as sent, or not yet, each with the reason in words. */
export function reviewFileNotices(attachments: ExternalNoteAttachment[] | undefined): { id: string; name: string; text: string; blocking: boolean }[] {
  const files = attachments || [];
  const byId = new Map(files.map((f) => [f.id, f]));
  const reason: Record<string, string> = {
    pending: 'still being received; validation waits for it',
    refetching: 'being requested again from the referrals platform; validation waits for it',
    failed: 'could not be received and will not be filed',
    expired: 'its link expired before it was received and will not be filed',
  };
  const notices: { id: string; name: string; text: string; blocking: boolean }[] = [];
  // Pages are reported per PDF: one line saying how many did not arrive, not one line per page.
  for (const [pdfId, pages] of pagesByPdf(files)) {
    const pending = pages.filter((p) => p.status === 'pending' || p.status === 'refetching').length;
    const lost = pages.filter((p) => p.status === 'failed' || p.status === 'expired').length;
    const pdf = byId.get(pdfId);
    const pdfName = (pdf && pdf.fileName) || 'PDF';
    if (pending) notices.push({ id: `${pdfId}:pages`, name: pdfName, text: `${pending} of ${pages.length} pages still being received; validation waits for them`, blocking: true });
    else if (lost && isStored(pdf)) notices.push({ id: `${pdfId}:pages`, name: pdfName, text: `${lost} of ${pages.length} pages could not be received; the pages will be made from the PDF after validating`, blocking: false });
  }
  for (const f of files.filter((x) => x.status !== 'stored' && x.relation !== 'page')) {
    const text = f.relation === 'annotation' && f.status !== 'pending'
      ? 'an annotated version could not be received; the original is filed without it'
      : reason[f.status] || f.status;
    notices.push({ id: f.id, name: f.fileName || 'Attachment', text, blocking: f.status === 'pending' || f.status === 'refetching' });
  }
  return notices;
}

/* ── validation ───────────────────────────────────────────────────────────────────── */

export const REFERRAL_REASON_MAX = 5000;
/** The longest summary a referral can carry (the contract's bound). */
export const REFERRAL_SUMMARY_MAX = 20000;
/** The longest "Instruct MedicAI" instruction for a summary draft (the server refuses longer). */
export const REFERRAL_SUMMARY_INSTRUCTION_MAX = 2000;
export const REFERRAL_RECIPIENTS_MAX = 20;
export const DISMISS_REASON_MAX = 2000;

/**
 * §25: WHAT MAY BE SENT to the chosen recipients - the modes every one of them accepts.
 *
 * A clinician outside MedicAI is on the referral dashboard, which is not where a practice's records live: they receive a
 * referral note. Another MedicAI practice is a subscriber, and everything a referral can carry is open to them. Choosing
 * both at once therefore leaves only what both accept, which is the note. An option that does not say (an older server)
 * is read from what it is, so the narrower answer is the one that survives.
 *
 * This is the dialog's copy of the rule; practice.js enforces it, and refuses what this lets through.
 */
export function allowedShareModes(
  options: readonly ReferralRecipientOption[],
  linkIds: readonly string[],
): ReferralShareMode[] {
  const chosen = options.filter((o) => linkIds.some((id) => id.toLowerCase() === o.linkId.toLowerCase()));
  if (!chosen.length) return [...SHARE_MODES_ALL];
  return SHARE_MODES_ALL.filter((mode) => chosen.every((o) => modesForOption(o).includes(mode)));
}

const SHARE_MODES_ALL: readonly ReferralShareMode[] = ['full', 'summary', 'items'];

const modesForOption = (option: ReferralRecipientOption): readonly ReferralShareMode[] => {
  if (option.shareModes && option.shareModes.length) return option.shareModes;
  if (option.shareCeiling) return option.shareCeiling === 'full' ? SHARE_MODES_ALL : ['summary'];
  return option.targetType === 'group' ? SHARE_MODES_ALL : ['summary'];
};

/** True when every chosen recipient is capped at a referral note: the form has no "what to share" choice to make. */
export const letterOnly = (options: readonly ReferralRecipientOption[], linkIds: readonly string[]): boolean => {
  const modes = allowedShareModes(options, linkIds);
  return modes.length === 1 && modes[0] === 'summary';
};

/** The reasons the form cannot be submitted yet, in the order the form reads. Empty means it can be. */
export function newReferralProblems(form: NewReferralForm, now: Date = new Date()): string[] {
  const problems: string[] = [];
  if (!form.linkIds.length) problems.push('Choose at least one recipient.');
  if (form.linkIds.length > REFERRAL_RECIPIENTS_MAX) problems.push(`Choose at most ${REFERRAL_RECIPIENTS_MAX} recipients.`);
  const reason = (form.reason || '').trim();
  if (!reason) problems.push('Enter the reason for the referral.');
  if (reason.length > REFERRAL_REASON_MAX) problems.push(`The reason can be at most ${REFERRAL_REASON_MAX} characters.`);
  if (!form.urgency) problems.push('Choose an urgency.');
  if (form.shareMode === 'summary') {
    // A summary is point in time: no future-sharing choice to make (§15, S1).
    const summary = (form.summaryText || '').trim();
    if (!summary) problems.push('Generate or write the summary to share.');
    if (summary.length > REFERRAL_SUMMARY_MAX) problems.push(`The summary can be at most ${REFERRAL_SUMMARY_MAX} characters.`);
    if ((form.summaryFileRefs || []).length > REFERRAL_SUMMARY_FILES_MAX) problems.push(`Choose at most ${REFERRAL_SUMMARY_FILES_MAX} files.`);
  } else {
    if (!form.futureMode) problems.push('Choose whether future entries are shared.');
    if (form.futureMode === 'until') {
      const until = form.futureUntil ? new Date(form.futureUntil) : null;
      if (!until || Number.isNaN(until.getTime()) || until.getTime() <= now.getTime()) problems.push('Choose a date in the future.');
    }
  }
  if (!form.acknowledged) problems.push('Confirm that shared records cannot be unshared.');
  return problems;
}

/**
 * The create-referral request for a form. A summary referral sends its summary and future mode 'none'; sending it is the
 * physician's confirmation that they reviewed it.
 */
export function referralRequestBody(form: NewReferralForm, acknowledgementVersion: string): Record<string, unknown> {
  const summary = form.shareMode === 'summary';
  return {
    linkIds: form.linkIds, reason: (form.reason || '').trim(), urgency: form.urgency, acknowledged: form.acknowledged, acknowledgementVersion,
    shareMode: form.shareMode,
    futureMode: summary ? 'none' : form.futureMode,
    futureUntil: !summary && form.futureMode === 'until' && form.futureUntil ? new Date(form.futureUntil).toISOString() : null,
    summary: summary ? { text: (form.summaryText || '').trim(), aiAssisted: form.summaryAiAssisted } : null,
    summaryReviewed: summary,
    summaryFiles: summary ? [...(form.summaryFileRefs || [])] : [],
  };
}

/** A new AI draft replaces the summary text. It counts as AI-assisted from then on, even after edits. */
export function applySummaryDraft(form: NewReferralForm, draft: Pick<SummaryDraft, 'text'>): NewReferralForm {
  return { ...form, summaryText: draft.text, summaryAiAssisted: true };
}

/** A piece of a streamed summary draft, as the server sends it on 'aiOutputUpdate-referralSummary'. */
export interface SummaryStreamEvent {
  streamId: string;
  results?: string;
  completed?: boolean;
  failed?: boolean;
}

/**
 * The draft text after a streamed piece, or null when the piece belongs to another dialog's stream (the practice's room
 * carries every member's drafts).
 */
export function applySummaryStream(text: string, event: SummaryStreamEvent, streamId: string | null): { text: string; done: boolean } | null {
  if (!streamId || event?.streamId !== streamId) return null;
  return { text: text + (event.results || ''), done: event.completed === true };
}

/** A line under the draft: what it was made from. */
export function summaryDraftNote(draft: Pick<SummaryDraft, 'includedEntries' | 'omittedEntries'>): string {
  const covered = `Drafted by AI from ${draft.includedEntries} timeline ${draft.includedEntries === 1 ? 'entry' : 'entries'}`;
  return draft.omittedEntries > 0
    ? `${covered}; ${draft.omittedEntries} older ${draft.omittedEntries === 1 ? 'entry was' : 'entries were'} too much to include. Check it against the file.`
    : `${covered}. Check it against the file before sending.`;
}

/**
 * The AI-token gate for the summary draft (architecture §16, T1): exactly Cura's lock (core/utils/ai-token-gate.ts). Writing
 * the summary by hand is never gated. The server refuses the same way (402 AI_BALANCE_EXHAUSTED) as the backstop.
 */
export type SummaryAiGate = AiTokenGate;
export { AI_LOW_BALANCE_LIMIT };
export const summaryAiGate = aiTokenGate;

export const AI_TOKENS_LOCKED_MESSAGE =
  'You do not have enough AI tokens to generate a summary. Recharge your token balance in Settings, under "Subscriptions & Usage", or write the summary yourself.';

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const isPlausibleEmail = (value: string | null | undefined): boolean => EMAIL_SHAPE.test(String(value || '').trim());

/* ── errors ───────────────────────────────────────────────────────────────────────── */

const ERROR_MESSAGES: Record<string, string> = {
  REFERRALS_NOT_ENABLED: 'Referrals are not switched on for this practice yet.',
  ALREADY_IN_PRACTICE: 'This person is already a member of your practice. There is no need to invite them.',
  PRACTICE_REFERRALS_NOT_ENABLED: 'Referrals between practices are not switched on yet.',
  CODE_NOT_RECOGNISED: 'No practice has this referral code. Check it with the practice: a code changes when the practice renews it.',
  REFERRAL_CODE_RATE_LIMITED: 'Too many referral codes entered. Please try again in an hour.',
  LINK_REQUEST_PENDING: 'Accept or decline this request instead.',
  ALREADY_DECIDED: 'Someone in your practice has already accepted or declined this referral.',
  PATIENT_NOT_FOUND: 'That patient is not in your practice.',
  REPLY_EXISTS: 'Your practice already shares back on this referral.',
  SUPERSEDED: 'A newer change replaced this one: review that instead.',
  ALREADY_RESOLVED: 'Someone in your practice has already reviewed this change.',
  DECISIONS_INCOMPLETE: 'Accept or reject every changed detail.',
  ROLE_NOT_PERMITTED: 'Your role in this practice does not allow this.',
  NOT_A_GROUP_MEMBER: 'You do not have access to this practice.',
  NOT_FOUND: 'This item no longer exists or belongs to another practice.',
  RECIPIENT_UNAVAILABLE: 'A chosen recipient is no longer in your referral network. Reload and choose again.',
  REFERRAL_DATA_INVALID: "Some of this patient's details cannot be sent in a referral. Please check the patient's details.",
  ALREADY_DISMISSED: 'This note was already dismissed by someone else.',
  ALREADY_VALIDATED: 'This note is already on the timeline.',
  EXTERNAL_NOTE_READ_ONLY: 'This note was written by an external clinician and cannot be changed.',
  AI_BALANCE_EXHAUSTED: 'Your practice has no AI tokens left. Top up to generate a summary, or write the summary yourself.',
  SUMMARY_UNAVAILABLE: 'The summary could not be generated right now. Try again, or write the summary yourself.',
  SUMMARY_FILE_INVALID: 'A chosen file is no longer in the patient file. Choose the files again.',
  SUMMARY_FILES_TOO_MANY: `Choose at most ${REFERRAL_SUMMARY_FILES_MAX} files.`,
  SUMMARY_FILES_TOO_MANY_PAGES: 'These files have too many pages to send together. Choose fewer PDFs.',
  NOTHING_TO_REQUEST: 'There are no files to ask for again.',
  FILES_CHANGED: 'The files changed while asking for them again. Reload and try again.',
  ALREADY_REVIEWED: 'This note has already been reviewed.',
};

/**
 * The message a screen shows for a failed request. The server's own message is used for its validation refusals
 * (they are written for users); known codes get fixed wording; anything else gets the fallback, never a raw error.
 */
/** Demographic fields by the names the patient details form uses (for messages about a referral's patient data). */
export const REFERRAL_PATIENT_FIELD_LABELS: Record<string, string> = {
  title: 'Title', initials: 'Initials', name: 'First name', surname: 'Surname', dob: 'Date of birth', gender: 'Gender', idType: 'ID type',
  idNumber: 'ID number', passportNumber: 'Passport number', passportCountry: 'Passport country', email: 'Email', phoneNumber: 'Phone',
  mobileNumber: 'Mobile', address: 'Address', addressCode: 'Postal code', country: 'Country', medicalAid: 'Medical aid',
  medicalSchemeCode: 'Medical aid scheme code', medicalPlanCode: 'Medical aid plan code', medicalPlanOptionCode: 'Medical aid plan option code',
  medicalAidNumber: 'Medical aid number', medicalAidPlan: 'Medical aid plan', medicalMainMember: 'Main member', medicalDependantCode: 'Dependant code',
  dateOfDeath: 'Date of death', reason: 'Reason for referral', summary: 'Summary',
};

/** "Initials and Medical aid number" from the fields a referral named, in the form's words. */
export function referralFieldNames(paths: readonly string[]): string {
  const names = [...new Set(paths.map((p) => {
    const parts = String(p).split('.');
    const field = parts[0] === 'patient' ? parts[1] : parts[0];
    return REFERRAL_PATIENT_FIELD_LABELS[field ?? ''] ?? null;
  }).filter((n): n is string => !!n))];
  return names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export function describeReferralFailure(error: unknown, fallback: string): string {
  const body = (error as { error?: { error?: unknown; message?: unknown; details?: unknown } } | null)?.error;
  const code = typeof body?.error === 'string' ? body.error : null;
  // Which details cannot travel, named, rather than "some details".
  if (code === 'REFERRAL_DATA_INVALID' && Array.isArray(body?.details)) {
    const fields = referralFieldNames(body?.details as string[]);
    if (fields) return `${fields} cannot be sent in a referral as recorded. Please check ${fields.includes(' and ') ? 'them' : 'it'} in the patient's details.`;
  }
  if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
  const status = (error as { status?: unknown } | null)?.status;
  if (status === 400 && typeof body?.message === 'string' && body.message.length <= 200) return body.message;
  if (status === 0) return 'The server could not be reached. Check your connection and try again.';
  return fallback;
}
